import { useMemo, useReducer } from 'react';
import { getTimeSeriesPeriodMap } from '@/utilities/timeSeriesPeriodMap';

type SeriesData<T extends string | number> = { [key in T]: number[][] };
type SeriesEntity<T extends string | number> = { [key in T]: number };
export type SeriesFilter<T extends string | number> = { [key in T]: boolean };

export interface Entities<T extends string | number> {
  [data: number]: SeriesEntity<T>;
}

interface InitialisationPayload<T extends string | number> {
  data: SeriesData<T>;
  dayZero?: number;
  defaultFilter: SeriesFilter<T>;
  hideToggleFilter?: boolean;
}

interface FilterPayload<T extends string | number> {
  key: string | string[];
  data: SeriesData<T>;
}

interface State<T extends string | number> {
  entities: Entities<T>;
  filter: SeriesFilter<T>;
  series: string[];
  periods: ReturnType<typeof getTimeSeriesPeriodMap>;
  keys: number[];
  disabled: Partial<SeriesFilter<T>>;
  minimumX: number;
  maximumX: number;
  dayZero?: number;
}

type Action<T extends string | number> =
  | { type: 'filter'; payload: FilterPayload<T> }
  | { type: 'reset'; payload: InitialisationPayload<T> };

const getEntities = <T extends string | number>(
  data: SeriesData<T>,
  series: string[]
): Entities<T> => {
  const entities = {};

  series.forEach((key) => {
    data[key].forEach((n) => {
      entities[n[0]] = entities[n[0]] ?? {};
      entities[n[0]][key] = n[1];
    });
  });

  return entities;
};

type PartialState<T extends string | number> = Pick<
  State<T>,
  'keys' | 'minimumX' | 'maximumX' | 'periods' | 'entities' | 'disabled'
>;

const getPartialState = <T extends string | number>(
  data: SeriesData<T>,
  series: string[],
  filter: SeriesFilter<T>,
  dayZero?: number
): PartialState<T> => {
  const entities = getEntities<T>(
    data,
    series.filter((n) => filter[n])
  );

  const keys = Object.keys(entities)
    .map((n) => Number(n))
    .sort((a, b) => a - b);

  let nextDayZero = keys.length > 0 ? keys[keys.length - 1] : 0;

  if (dayZero && dayZero < nextDayZero) {
    nextDayZero = dayZero;
  }

  const periods = getTimeSeriesPeriodMap(new Date(nextDayZero));

  const minimumX = Math.min(...keys);
  const maximumX = Math.max(...keys);

  const disabled: Partial<SeriesFilter<T>> = series.reduce(
    (a, b) => ({
      ...a,
      [b]: data[b].length === 0,
    }),
    {}
  );

  return {
    keys,
    minimumX,
    maximumX,
    periods,
    entities,
    disabled,
  };
};

const getInitialisation =
  <T extends string | number>() =>
  (payload: InitialisationPayload<T>): State<T> => {
    const { defaultFilter, data, dayZero } = payload;
    const series = Object.keys(data);
    const { keys, entities, periods, minimumX, maximumX, disabled } =
      getPartialState(data, series, defaultFilter, dayZero);
    return {
      filter: defaultFilter,
      series,
      keys,
      entities,
      periods,
      minimumX,
      maximumX,
      disabled,
      dayZero,
    };
  };

const getReducer =
  <T extends string | number>() =>
  (state: State<T>, action: Action<T>): State<T> => {
    switch (action.type) {
      case 'filter': {
        const { key, data } = action.payload;
        const { filter, series, dayZero } = state;
        const nextFilter = {
          ...filter,
        };

        if (typeof key === 'string') {
          nextFilter[key] = !filter[key];
        } else {
          key.forEach((n) => {
            nextFilter[n] = !filter[n];
          });
        }

        const { keys, entities, periods, minimumX, maximumX, disabled } =
          getPartialState(data, series, nextFilter, dayZero);

        return {
          ...state,
          keys,
          minimumX,
          maximumX,
          periods,
          filter: nextFilter,
          entities,
          disabled,
        };
      }
      case 'reset': {
        const init = getInitialisation<T>();
        return init(action.payload);
      }
      default:
        throw new Error();
    }
  };

interface UseTimeSeriesProps<T extends string | number> {
  data: SeriesData<T>;
  dayZero?: number;
  defaultFilter: SeriesFilter<T>;
  hideToggleFilter?: boolean;
}

export default function useTimeSeriesReducer<T extends string | number>({
  data,
  dayZero,
  defaultFilter,
  hideToggleFilter,
}: UseTimeSeriesProps<T>) {
  const [
    { entities, keys, minimumX, maximumX, periods, filter, series, disabled },
    dispatch,
  ] = useReducer(
    getReducer<T>(),
    {
      data,
      dayZero,
      defaultFilter,
    },
    getInitialisation<T>()
  );

  const resolvedSeries = useMemo(() => {
    if (hideToggleFilter && typeof defaultFilter !== 'undefined') {
      return series.filter((s) => {
        return defaultFilter[s];
      });
    }
    return series;
  }, [defaultFilter, hideToggleFilter, series]);

  return {
    entities,
    keys,
    minimumX,
    maximumX,
    periods,
    filter,
    series: resolvedSeries,
    disabled,
    dispatch,
  };
}
